import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "ahooks";
import {
  fetchJson,
  postJson,
  postForm,
  putJson,
  semanticIdempotencyKey,
  setCsrfToken,
} from "./fetcher";
import type {
  ApiEnvelope,
  Backtest,
  BacktestAudit,
  BacktestMetrics,
  BacktestOptions,
  BacktestTrade,
  DiagnosticBacktest,
  Bar,
  CandidateSnapshot,
  CatalogSummary,
  Dashboard,
  Dataset,
  Instrument,
  InstrumentContext,
  KlineInterval,
  KlineSeries,
  ResearchCandidate,
  ResearchRun,
  Strategy,
  StrategyTemplate,
  StrategyVersion,
  EquityPoint,
  Job,
  PaperAccount,
  PaperExecutionStrategyVersion,
  PaperFill,
  PaperOrder,
  PaperPosition,
  PaperStrategyRun,
  PortfolioBacktest,
  RiskEvent,
  Session,
  Settings,
  Provider,
  DatasetDetail,
  QualityEvent,
  ImportJob,
  IndexPool,
  IndexPoolMember,
  ResearchFactor,
  ResearchEvent,
  SystemMetrics,
  StockPool,
  StockPoolBacktestResult,
  StockPoolConsumption,
  StockPoolDiff,
  StockPoolMember,
  StockPoolPaperResult,
  StockPoolRun,
  StockPoolRunResult,
  StockPoolVersion,
} from "./types";
import { unwrapItems, unwrapPage } from "./types";
import type { components } from "./generated";

function postTask<T, B>(scope: string, path: string, body: B) {
  return postJson<T, B>(path, body, semanticIdempotencyKey(scope, body));
}

const paged = <T>(path: string, limit: number, offset: number) =>
  fetchJson<ApiEnvelope<T>>(path).then(payload => unwrapPage(payload, limit, offset));

export const apiQueryKeys = {
  dashboard: ["dashboard"] as const,
  catalogSummary: ["catalog", "summary"] as const,
  datasets: ["catalog", "datasets"] as const,
  instruments: (params: Record<string, string>) =>
    ["market", "instruments", params] as const,
  bars: (instrumentId: string, datasetVersion: string, limit: number) =>
    ["market", "bars", instrumentId, datasetVersion, limit] as const,
  klines: (instrumentId: string, interval: KlineInterval, datasetVersion: string, limit: number) =>
    ["market", "klines", instrumentId, interval, datasetVersion, limit] as const,
  instrumentContext: (instrumentId: string) =>
    ["market", "instrument-context", instrumentId] as const,
  strategies: ["strategies"] as const,
  strategyVersion: (id: string) => ["strategy-version", id] as const,
  strategyTemplates: ["strategy-templates"] as const,
  strategyTemplate: (key: string) => ["strategy-template", key] as const,
  backtestOptions: ["backtest-options"] as const,
  researchRuns: (params?: Record<string, string>) =>
    ["research", "runs", params ?? {}] as const,
  researchRun: (id: string) => ["research", "run", id] as const,
  candidates: (id: string, params: Record<string, string>) => ["research", "candidates", id, params] as const,
  backtests: (params?: Record<string, string>) =>
    ["backtests", params ?? {}] as const,
  backtest: (id: string) => ["backtest", id] as const,
  backtestTrades: (id: string, limit: number, offset: number) => ["backtest", id, "trades", limit, offset] as const,
  backtestMetrics: (id: string) => ["backtest", id, "metrics"] as const,
  backtestAudit: (id: string, limit: number, offset: number) => ["backtest", id, "audit", limit, offset] as const,
  portfolioBacktests: (params?: Record<string, string>) => ["backtests", "portfolio", params ?? {}] as const,
  portfolioBacktest: (id: string) => ["backtest", "portfolio", id] as const,
  diagnosticBacktest: (id: string, limit: number, offset: number) => ["diagnostic-backtest", id, limit, offset] as const,
  paperStrategyRuns: (params: Record<string, string>) => ["paper", "strategy-runs", params] as const,
  paperStrategyRun: (id: string) => ["paper", "strategy-run", id] as const,
  paperStrategyRunOrders: (id: string, limit: number, offset: number) => ["paper", "strategy-run", id, "orders", limit, offset] as const,
  paperStrategyRunRisks: (id: string, limit: number, offset: number) => ["paper", "strategy-run", id, "risk-events", limit, offset] as const,
  paperAccounts: (params: Record<string, string>) => ["paper", "accounts", params] as const,
  paperAccount: (id: string) => ["paper", "account", id] as const,
  paperPositions: (id: string, params: Record<string, string>) =>
    ["paper", "account", id, "positions", params] as const,
  paperOrders: (id: string, params: Record<string, string>) => ["paper", "account", id, "orders", params] as const,
  paperFills: (id: string, params: Record<string, string>) => ["paper", "account", id, "fills", params] as const,
  paperEquity: (id: string) => ["paper", "account", id, "equity"] as const,
  riskEvents: (params: Record<string, string>) =>
    ["risk", "events", params] as const,
  jobs: (params: Record<string, string>) => ["jobs", params] as const,
  job: (id: string) => ["job", id] as const,
  settings: ["settings"] as const,
  session: ["session"] as const,
  providers: ["data", "providers"] as const,
  dataset: (version: string) => ["catalog", "dataset", version] as const,
  qualityEvents: (params: Record<string, string>) =>
    ["quality", "events", params] as const,
  importJob: (id: string) => ["imports", id] as const,
  researchFactors: (params: Record<string, string>) =>
    ["research", "factors", params] as const,
  researchEvents: (params: Record<string, string>) =>
    ["research", "events", params] as const,
  auditEvents: ["audit", "events"] as const,
  systemMetrics: ["system", "metrics"] as const,
  strategyVersions: ["strategy-versions", "frozen"] as const,
  indexPools: ["index-pools"] as const,
  indexPoolMembers: (code: string, params: Record<string, string>) =>
    ["index-pool", code, "members", params] as const,
  stockPools: (params: Record<string, string>) => ["stock-pools", params] as const,
  stockPool: (id: string) => ["stock-pool", id] as const,
  stockPoolVersions: (id: string, params: Record<string, string>) => ["stock-pool", id, "versions", params] as const,
  stockPoolRuns: (id: string, params: Record<string, string>) => ["stock-pool", id, "runs", params] as const,
  stockPoolMembers: (id: string, versionId: string, params: Record<string, string>) =>
    ["stock-pool", id, "version", versionId, "members", params] as const,
  stockPoolDiff: (id: string, versionId: string) =>
    ["stock-pool", id, "version", versionId, "diff"] as const,
  stockPoolConsumptions: (id: string, versionId: string, params?: Record<string, string>) =>
    ["stock-pool", id, "version", versionId, "consumptions", params ?? {}] as const,
  stockPoolPaperStrategies: (id: string, versionId: string) =>
    ["stock-pool", id, "version", versionId, "paper-strategies"] as const,
  dailyFoundationSyncs: ["data", "daily-foundation", "syncs"] as const,
  coarseScreens: ["data", "coarse-screens"] as const,
  coarseScreenMembers: (id: string, status: string) =>
    ["data", "coarse-screen", id, "members", status] as const,
};
export function useDashboard() {
  return useQuery({
    queryKey: apiQueryKeys.dashboard,
    queryFn: () => fetchJson<Dashboard>("/api/v2/dashboard"),
  });
}
export function useCatalogSummary() {
  return useQuery({
    queryKey: apiQueryKeys.catalogSummary,
    queryFn: () => fetchJson<CatalogSummary>("/api/v2/catalog/summary"),
  });
}
export function useDatasets() {
  return useQuery({
    queryKey: apiQueryKeys.datasets,
    queryFn: async () =>
      unwrapItems(
        await fetchJson<ApiEnvelope<Dataset>>("/api/v2/catalog/datasets?limit=200"),
      ),
  });
}
export function useDatasetPage(limit: number, offset: number) {
  return useQuery({
    queryKey: ["catalog", "dataset-page", limit, offset],
    queryFn: () => paged<Dataset>(`/api/v2/catalog/datasets?limit=${limit}&offset=${offset}`, limit, offset),
  });
}
export function useProviders() {
  return useQuery({
    queryKey: apiQueryKeys.providers,
    queryFn: () =>
      fetchJson<ApiEnvelope<Provider>>("/api/v2/providers").then(unwrapItems),
  });
}
export function useDataset(version: string) {
  return useQuery({
    enabled: Boolean(version),
    queryKey: apiQueryKeys.dataset(version),
    queryFn: () =>
      fetchJson<DatasetDetail>(
        `/api/v2/datasets/${encodeURIComponent(version)}`,
      ),
  });
}
export function useQualityEvents(
  params: { limit?: number; offset?: number } = {},
) {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 100),
    offset: String(params.offset ?? 0),
  });
  return useQuery({
    queryKey: apiQueryKeys.qualityEvents({
      limit: String(params.limit ?? 100),
      offset: String(params.offset ?? 0),
    }),
    queryFn: () =>
      fetchJson<ApiEnvelope<QualityEvent>>(
        `/api/v2/quality/events?${query.toString()}`,
      ).then(unwrapItems),
  });
}
export function useResearchFactors(params: {
  runId?: string;
  factor?: string;
  date?: string;
}) {
  const query = new URLSearchParams();
  if (params.runId) query.set("run_id", params.runId);
  if (params.factor) query.set("factor", params.factor);
  if (params.date) query.set("date", params.date);
  return useQuery({
    enabled: Boolean(params.runId),
    queryKey: apiQueryKeys.researchFactors({
      run_id: params.runId ?? "",
      factor: params.factor ?? "",
      date: params.date ?? "",
    }),
    queryFn: () =>
      fetchJson<ApiEnvelope<ResearchFactor>>(
        `/api/v2/research/factors?${query.toString()}`,
      ).then(unwrapItems),
  });
}
export function useResearchEvents(params: {
  severity?: string;
  from?: string;
  limit?: number;
  offset?: number;
}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (params.severity) query.set("status", params.severity);
  if (params.from) query.set("date", params.from);
  const key = Object.fromEntries(query);
  return useQuery({
    queryKey: apiQueryKeys.researchEvents(key),
    queryFn: () => paged<ResearchEvent>(`/api/v2/research/events?${query.toString()}`, limit, offset),
  });
}
export function useAuditEvents() {
  return useQuery({
    queryKey: apiQueryKeys.auditEvents,
    queryFn: () =>
      fetchJson<ApiEnvelope<ResearchEvent>>("/api/v2/audit/events").then(
        unwrapItems,
      ),
  });
}
export function useSystemMetrics() {
  return useQuery({
    queryKey: apiQueryKeys.systemMetrics,
    queryFn: () => fetchJson<SystemMetrics>("/api/v2/system/metrics"),
  });
}
export function useImportJob(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.importJob(id),
    queryFn: () =>
      fetchJson<ImportJob>(`/api/v2/imports/${encodeURIComponent(id)}`),
    refetchInterval: (query) =>
      ["queued", "running", "retrying"].includes(
        String(query.state.data?.status),
      )
        ? 2000
        : false,
  });
}
export function useInstruments(params: {
  search: string;
  market: string;
  status: string;
  limit: number;
}) {
  const search = useDebounce(params.search, { wait: 280 });
  const query = new URLSearchParams();
  if (search) query.set("search", search);
  if (params.market) query.set("market", params.market);
  if (params.status) query.set("status", params.status);
  query.set("limit", String(params.limit));
  const searchKey = {
    search,
    market: params.market,
    status: params.status,
    limit: String(params.limit),
  };
  return useQuery({
    queryKey: apiQueryKeys.instruments(searchKey),
    queryFn: async () =>
      unwrapItems(
        await fetchJson<ApiEnvelope<Instrument>>(
          `/api/v2/market/instruments?${query.toString()}`,
        ),
      ),
  });
}
export function useInstrumentPage(params: { search: string; market: string; status: string; limit: number; offset: number }) {
  const search = useDebounce(params.search, { wait: 280 });
  const query = new URLSearchParams({ limit: String(params.limit), offset: String(params.offset) });
  if (search) query.set("search", search);
  if (params.market) query.set("market", params.market);
  if (params.status) query.set("status", params.status);
  const key = Object.fromEntries(query);
  return useQuery({
    queryKey: apiQueryKeys.instruments(key),
    queryFn: () => paged<Instrument>(`/api/v2/market/instruments?${query.toString()}`, params.limit, params.offset),
  });
}

export function useBars(
  instrumentId: string,
  datasetVersion: string,
  limit: number,
) {
  const query = new URLSearchParams({ limit: String(limit) });
  if (datasetVersion) query.set("dataset_version", datasetVersion);
  return useQuery({
    enabled: Boolean(instrumentId),
    queryKey: apiQueryKeys.bars(instrumentId, datasetVersion, limit),
    queryFn: async () =>
      unwrapItems(
        await fetchJson<ApiEnvelope<Bar>>(
          `/api/v2/market/instruments/${encodeURIComponent(instrumentId)}/bars?${query.toString()}`,
        ),
      ),
  });
}
export function useKlines(
  instrumentId: string,
  interval: KlineInterval,
  datasetVersion: string,
  limit: number,
) {
  const query = new URLSearchParams({
    bar_interval: interval,
    limit: String(limit),
  });
  if (datasetVersion) query.set("dataset_version", datasetVersion);
  return useQuery({
    enabled: Boolean(instrumentId),
    queryKey: apiQueryKeys.klines(instrumentId, interval, datasetVersion, limit),
    queryFn: () =>
      fetchJson<KlineSeries>(
        `/api/v2/market/instruments/${encodeURIComponent(instrumentId)}/klines?${query.toString()}`,
      ),
  });
}
export function useInstrumentContext(instrumentId: string) {
  return useQuery({
    enabled: Boolean(instrumentId),
    queryKey: apiQueryKeys.instrumentContext(instrumentId),
    queryFn: () =>
      fetchJson<InstrumentContext>(
        `/api/v2/market/instruments/${encodeURIComponent(instrumentId)}/context`,
      ),
  });
}

export function useStrategies() {
  return useQuery({
    queryKey: apiQueryKeys.strategies,
    queryFn: async () =>
      unwrapItems(await fetchJson<ApiEnvelope<Strategy>>("/api/v2/strategies")),
  });
}
export function useStrategyVersion(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.strategyVersion(id),
    queryFn: () =>
      fetchJson<StrategyVersion>(
        `/api/v2/strategy-versions/${encodeURIComponent(id)}`,
      ),
  });
}
export function useFrozenStrategyVersions() {
  return useQuery({
    queryKey: apiQueryKeys.strategyVersions,
    queryFn: async () =>
      unwrapItems(
        await fetchJson<ApiEnvelope<StrategyVersion>>(
          "/api/v2/strategy-versions?status=frozen&limit=200",
        ),
      ),
  });
}
export function useStrategyTemplates() {
  return useQuery({
    queryKey: apiQueryKeys.strategyTemplates,
    queryFn: () => fetchJson<{ items?: StrategyTemplate[] }>("/api/v2/strategy-templates").then(value => value.items ?? []),
  });
}
export function useStrategyTemplate(key: string) {
  return useQuery({
    enabled: Boolean(key),
    queryKey: apiQueryKeys.strategyTemplate(key),
    queryFn: () => fetchJson<StrategyTemplate>(`/api/v2/strategy-templates/${encodeURIComponent(key)}`),
  });
}
export function useBacktestOptions() {
  return useQuery({
    queryKey: apiQueryKeys.backtestOptions,
    queryFn: () => fetchJson<BacktestOptions>("/api/v2/backtests/options"),
  });
}
export function useResearchRuns(params: { status?: string; limit?: number; offset?: number }) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (params.status) query.set("status", params.status);
  const key = Object.fromEntries(query);
  return useQuery({
    queryKey: apiQueryKeys.researchRuns(key),
    queryFn: () => paged<ResearchRun>(`/api/v2/research/runs?${query.toString()}`, limit, offset),
  });
}
export function useResearchRun(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.researchRun(id),
    queryFn: () =>
      fetchJson<ResearchRun>(`/api/v2/research/runs/${encodeURIComponent(id)}`),
  });
}
export function useResearchCandidates(id: string, params: { eligibility?: "eligible" | "rejected" | "all"; search?: string; limit?: number; offset?: number } = {}) {
  const query = new URLSearchParams({
    eligibility: params.eligibility ?? "all",
    search: params.search ?? "",
    limit: String(params.limit ?? 25),
    offset: String(params.offset ?? 0),
  });
  const key = Object.fromEntries(query);
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.candidates(id, key),
    queryFn: async () => {
      const response = await fetchJson<CandidateSnapshot | ApiEnvelope<ResearchCandidate>>(
        `/api/v2/research/runs/${encodeURIComponent(id)}/candidates?${query.toString()}`,
      );
      return "candidates" in (response as object)
        ? (response as CandidateSnapshot)
        : { candidates: unwrapItems(response as ApiEnvelope<ResearchCandidate>) };
    },
  });
}
export function useBacktests(params: { status?: string; search?: string; limit?: number; offset?: number }) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);
  const key = Object.fromEntries(query);
  return useQuery({
    queryKey: apiQueryKeys.backtests(key),
    queryFn: () => paged<Backtest>(`/api/v2/backtests?${query.toString()}`, limit, offset),
  });
}
export function usePortfolioBacktests(params: { status?: string; search?: string; limit?: number; offset?: number }) {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);
  const key = Object.fromEntries(query);
  return useQuery({
    queryKey: apiQueryKeys.portfolioBacktests(key),
    queryFn: () => paged<PortfolioBacktest>(`/api/v2/backtests/portfolio?${query.toString()}`, limit, offset),
  });
}
export function usePortfolioBacktest(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.portfolioBacktest(id),
    queryFn: () => fetchJson<PortfolioBacktest>(`/api/v2/backtests/portfolio/${encodeURIComponent(id)}`),
  });
}
export function useBacktest(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.backtest(id),
    queryFn: () =>
      fetchJson<Backtest>(`/api/v2/backtests/${encodeURIComponent(id)}`),
  });
}
export function useBacktestTrades(id: string, limit = 20, offset = 0) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.backtestTrades(id, limit, offset),
    queryFn: () => paged<BacktestTrade>(`/api/v2/backtests/${encodeURIComponent(id)}/trades?limit=${limit}&offset=${offset}`, limit, offset),
  });
}
export function useBacktestMetrics(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.backtestMetrics(id),
    queryFn: () =>
      fetchJson<BacktestMetrics>(
        `/api/v2/backtests/${encodeURIComponent(id)}/metrics`,
      ),
  });
}
export function useBacktestAudit(id: string, limit = 20, offset = 0) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.backtestAudit(id, limit, offset),
    queryFn: () =>
      fetchJson<BacktestAudit>(
        `/api/v2/backtests/${encodeURIComponent(id)}/audit?limit=${limit}&offset=${offset}`,
      ),
  });
}

export type CreateStrategyInput = { name: string; description: string };
export type CreateResearchRunInput = {
  strategy_version_id: number;
  dataset_version: string;
  signal_ts?: string;
  universe_mode: "point_in_time_market" | "selected_instruments" | "index_pool";
  index_pool_code?: "000300" | "000905";
  instrument_ids: string[];
};
export type CreatePortfolioBacktestInput = {
  strategy_version_id: number;
  dataset_version: string;
  start_date: string;
  end_date: string;
  top_k: number;
  execution_model_version: string;
  participation_rate: string;
  initial_cash: string;
  universe_mode: "point_in_time_market" | "selected_instruments";
  instrument_ids: string[];
};
export type CreateBacktestInput = {
  instrument_id: string;
  strategy_version_id: number;
  dataset_version: string;
  start_date: string;
  end_date: string;
  execution_model_version: string;
  quantity: number;
  participation_rate: string;
  initial_cash: string;
};
export function createStrategy(input: CreateStrategyInput) {
  return postJson<Strategy, CreateStrategyInput>("/api/v2/strategies", input);
}
export function createStrategyTemplateVersion(key: string) {
  return postTask<StrategyVersion, Record<string, never>>(
    `strategy-template:${key}`,
    `/api/v2/strategy-templates/${encodeURIComponent(key)}/versions`,
    {},
  );
}
export function createResearchRun(input: CreateResearchRunInput) {
  return postTask<ResearchRun, CreateResearchRunInput>(
    "research-run",
    "/api/v2/research/runs",
    input,
  );
}
export function createBacktest(input: CreateBacktestInput) {
  return postTask<Backtest, CreateBacktestInput>("backtest", "/api/v2/backtests", input);
}
export function createPortfolioBacktest(input: CreatePortfolioBacktestInput) {
  return postTask<PortfolioBacktest, CreatePortfolioBacktestInput>(
    "portfolio-backtest",
    "/api/v2/backtests/portfolio",
    input,
  );
}
export function useDiagnosticBacktest(id: string, limit = 20, offset = 0) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.diagnosticBacktest(id, limit, offset),
    queryFn: () => fetchJson<DiagnosticBacktest>(`/api/v2/diagnostic-backtests/${encodeURIComponent(id)}?limit=${limit}&offset=${offset}`),
  });
}

const items = <T>(path: string) =>
  fetchJson<ApiEnvelope<T>>(path).then(unwrapItems);
export function usePaperStrategyRuns(params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    queryKey: apiQueryKeys.paperStrategyRuns(key),
    queryFn: () => paged<PaperStrategyRun>(`/api/v2/paper/strategy-runs?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function usePaperStrategyRun(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperStrategyRun(id),
    queryFn: () => fetchJson<PaperStrategyRun>(`/api/v2/paper/strategy-runs/${encodeURIComponent(id)}`),
  });
}
export function usePaperStrategyRunOrders(id: string, limit = 20, offset = 0) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperStrategyRunOrders(id, limit, offset),
    queryFn: () => paged<Record<string, unknown>>(`/api/v2/paper/strategy-runs/${encodeURIComponent(id)}/orders?limit=${limit}&offset=${offset}`, limit, offset),
  });
}
export function usePaperStrategyRunRisks(id: string, limit = 20, offset = 0) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperStrategyRunRisks(id, limit, offset),
    queryFn: () => paged<Record<string, unknown>>(`/api/v2/paper/strategy-runs/${encodeURIComponent(id)}/risk-events?limit=${limit}&offset=${offset}`, limit, offset),
  });
}
export function usePaperAccounts(params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    queryKey: apiQueryKeys.paperAccounts(key),
    queryFn: () => paged<PaperAccount>(`/api/v2/paper/accounts?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function usePaperAccount(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperAccount(id),
    queryFn: () =>
      fetchJson<PaperAccount>(
        `/api/v2/paper/accounts/${encodeURIComponent(id)}`,
      ),
  });
}
export function usePaperPositions(id: string, limit = 20, offset = 0) {
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperPositions(id, key),
    queryFn: () => paged<PaperPosition>(`/api/v2/paper/accounts/${encodeURIComponent(id)}/positions?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function usePaperOrders(id: string, limit = 20, offset = 0) {
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperOrders(id, key),
    queryFn: () => paged<PaperOrder>(`/api/v2/paper/accounts/${encodeURIComponent(id)}/orders?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function usePaperFills(id: string, limit = 20, offset = 0) {
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperFills(id, key),
    queryFn: () => paged<PaperFill>(`/api/v2/paper/accounts/${encodeURIComponent(id)}/fills?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function usePaperEquity(id: string) {
  return useQuery<EquityPoint[]>({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.paperEquity(id),
    queryFn: async (): Promise<EquityPoint[]> => {
      const value = await fetchJson<
        | ApiEnvelope<EquityPoint>
        | { equity_curve?: EquityPoint[]; items?: EquityPoint[] }
      >(`/api/v2/paper/accounts/${encodeURIComponent(id)}/equity`);
      if (Array.isArray(value)) return value as EquityPoint[];
      if (value && typeof value === "object" && "equity_curve" in value)
        return ((value as { equity_curve?: EquityPoint[] }).equity_curve ??
          []) as EquityPoint[];
      return unwrapItems(value as ApiEnvelope<EquityPoint>) as EquityPoint[];
    },
  });
}
export function useRiskEvents(params: {
  accountId?: string;
  severity?: string;
  resolved?: string;
  limit?: number;
  offset?: number;
}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (params.accountId) query.set("account_id", params.accountId);
  if (params.severity) query.set("severity", params.severity);
  if (params.resolved) query.set("resolved", params.resolved);
  const key = Object.fromEntries(query);
  return useQuery({
    queryKey: apiQueryKeys.riskEvents(key),
    queryFn: () => paged<RiskEvent>(`/api/v2/risk/events?${query.toString()}`, limit, offset),
  });
}
export function useJobs(params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    queryKey: apiQueryKeys.jobs(key),
    queryFn: () => paged<Job>(`/api/v2/jobs?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function useJob(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.job(id),
    queryFn: () => fetchJson<Job>(`/api/v2/jobs/${encodeURIComponent(id)}`),
  });
}
export function useSettings() {
  return useQuery({
    queryKey: apiQueryKeys.settings,
    queryFn: () => fetchJson<Settings>("/api/v2/settings"),
  });
}
export function useSession() {
  return useQuery({
    queryKey: apiQueryKeys.session,
    queryFn: async () => {
      const value = await fetchJson<Session>("/api/v2/auth/session");
      const token = value.csrf_token ?? value.csrfToken;
      if (token) setCsrfToken(token);
      return value;
    },
  });
}

export type CreatePaperAccountInput = {
  security: string;
  mode?: "historical" | "polling";
  initial_cash?: number;
};
export type PaperOrderInput = { side: "buy" | "sell"; quantity: number };
export function createPaperAccount(input: CreatePaperAccountInput) {
  return postJson<PaperAccount, CreatePaperAccountInput>(
    "/api/v2/paper/accounts",
    input,
  );
}
export function submitPaperOrder(id: string, input: PaperOrderInput) {
  return postJson<PaperOrder, PaperOrderInput>(
    `/api/v2/paper/accounts/${encodeURIComponent(id)}/orders`,
    input,
  );
}
export function advancePaperAccount(id: string) {
  return postJson<PaperAccount, Record<string, never>>(
    `/api/v2/paper/accounts/${encodeURIComponent(id)}/advance`,
    {},
  );
}
export function acknowledgeRisk(id: string) {
  return postJson<RiskEvent, Record<string, never>>(
    `/api/v2/risk/events/${encodeURIComponent(id)}/acknowledge`,
    {},
  );
}
export function cancelJob(id: string) {
  return postJson<Job, Record<string, never>>(
    `/api/v2/jobs/${encodeURIComponent(id)}/cancel`,
    {},
  );
}
export function retryJob(id: string) {
  return postJson<Job, Record<string, never>>(
    `/api/v2/jobs/${encodeURIComponent(id)}/retry`,
    {},
  );
}
export function updateSettings(input: Record<string, unknown>) {
  return putJson<Settings, Record<string, unknown>>("/api/v2/settings", input);
}
export type BaoStockImportInput = {
  securities: string[];
  start_date: string;
  end_date: string;
  dataset_version?: string;
  authorization_confirmed: boolean;
};
export type AkshareImportInput = {
  trade_date: string;
  securities: string[];
  dataset_version?: string;
  authorization_confirmed: boolean;
};
export function importCsv(input: {
  security: string;
  dataset_version?: string;
  authorization_confirmed: boolean;
  file: File;
}) {
  const form = new FormData();
  form.set("security", input.security);
  form.set("authorization_confirmed", String(input.authorization_confirmed));
  if (input.dataset_version) form.set("dataset_version", input.dataset_version);
  form.set("file", input.file);
  return postForm<ImportJob>("/api/v2/imports/csv", form);
}
export function importBaoStock(input: BaoStockImportInput) {
  return postTask<ImportJob, BaoStockImportInput>(
    "baostock-5m",
    "/api/v2/imports/baostock",
    input,
  );
}
export function importAkshareArchive(input: AkshareImportInput) {
  return postTask<ImportJob, AkshareImportInput>(
    "akshare-1m",
    "/api/v2/imports/akshare/archive",
    input,
  );
}

export type DailyFoundationSync = {
  id: string;
  dataset_version: string;
  as_of_date: string;
  start_date: string;
  status: string;
  expected_count: number;
  completed_count: number;
  failed_count: number;
  coverage_ratio: number;
  available_for_same_day_1430: boolean;
};
export type CoarseScreen = {
  id: string;
  dataset_version: string;
  as_of_date: string;
  status: string;
  universe_count: number;
  eligible_count: number;
  rejected_count: number;
  missing_data_count: number;
  snapshot_hash: string;
  max_input_ts: string;
  same_day_1430_eligible: boolean;
  rules?: { strategy_version_id?: number | null; ranking_profile?: string; top_k?: number | null; minimum_history_days?: number; liquidity_window_days?: number; [key: string]: unknown };
};
export type CoarseScreenMember = {
  instrument_id: string;
  status: "eligible" | "rejected" | "missing_data";
  rank?: number | null;
  metrics: Record<string, unknown>;
  reasons: string[];
  max_input_ts: string;
};

export function useDailyFoundationSyncs() {
  return useQuery({
    queryKey: apiQueryKeys.dailyFoundationSyncs,
    queryFn: () =>
      items<DailyFoundationSync>("/api/v2/daily-foundation/syncs?limit=20"),
    refetchInterval: 5_000,
  });
}
export function importDailyFoundation(input: {
  as_of_date: string;
  authorization_confirmed: boolean;
  mode?: "bootstrap" | "increment" | "repair";
  history_start_date?: string;
  lookback_calendar_days?: number;
  batch_size: number;
  dataset_version?: string;
}) {
  return postTask<ImportJob, typeof input>(
    "daily-foundation",
    "/api/v2/imports/baostock/daily-foundation",
    input,
  );
}
export function useCoarseScreens() {
  return useQuery({
    queryKey: apiQueryKeys.coarseScreens,
    queryFn: () => items<CoarseScreen>("/api/v2/coarse-screens?limit=20"),
    refetchInterval: 5_000,
  });
}
export function useCoarseScreen(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: [...apiQueryKeys.coarseScreens, id],
    queryFn: () => fetchJson<CoarseScreen>(`/api/v2/coarse-screens/${encodeURIComponent(id)}`),
  });
}
export function createCoarseScreen(input: {
  dataset_version: string;
  strategy_version_id?: number;
  ranking_profile: "tradability" | "stable_low_volatility" | "balanced_multifactor" | "trend_strength";
  top_k?: number;
  minimum_history_days: number;
  liquidity_window_days: number;
  minimum_average_turnover_amount: number;
  minimum_free_float_market_cap: number;
  exclude_st: boolean;
  exclude_halted: boolean;
  exclude_one_price_limit: boolean;
}) {
  return postJson<ImportJob, typeof input>("/api/v2/coarse-screens", input);
}
export function useCoarseScreenMembers(
  id: string | undefined,
  status = "eligible",
) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.coarseScreenMembers(id ?? "", status),
    queryFn: () =>
      items<CoarseScreenMember>(
        `/api/v2/coarse-screens/${encodeURIComponent(id ?? "")}/members?status=${encodeURIComponent(status)}&limit=1000`,
      ),
  });
}
export function createFineCollection(
  id: string,
  input: {
    start_date: string;
    end_date: string;
    authorization_confirmed: boolean;
    max_candidates: number;
  },
) {
  return postTask<ImportJob, typeof input>(
    `fine-collection:${id}`,
    `/api/v2/coarse-screens/${encodeURIComponent(id)}/fine-collection`,
    input,
  );
}

export type LoginInput = { password: string };
export async function login(input: LoginInput) {
  const value = await postJson<Session, LoginInput>(
    "/api/v2/auth/login",
    input,
  );
  const token = value.csrf_token ?? value.csrfToken;
  if (token) setCsrfToken(token);
  return value;
}
export async function logout() {
  const value = await postJson<Session, Record<string, never>>(
    "/api/v2/auth/logout",
    {},
  );
  setCsrfToken(undefined);
  return value;
}

export function useIndexPools() {
  return useQuery({
    queryKey: apiQueryKeys.indexPools,
    queryFn: () => fetchJson<ApiEnvelope<IndexPool>>("/api/v2/index-pools").then(unwrapItems),
    refetchInterval: 30_000,
  });
}
export function useIndexPoolMembers(code: string, limit = 50, offset = 0, search = "") {
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) query.set("search", search);
  const key = Object.fromEntries(query);
  return useQuery({
    enabled: Boolean(code),
    queryKey: apiQueryKeys.indexPoolMembers(code, key),
    queryFn: () => paged<IndexPoolMember>(`/api/v2/index-pools/${encodeURIComponent(code)}/members?${query}`, limit, offset),
  });
}
export function syncIndexPools() {
  return postJson<ImportJob, Record<string, never>>("/api/v2/index-pools/sync", {});
}

export type CreateStockPoolInput = components["schemas"]["StockPoolCreate"];
export function useStockPools(params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    queryKey: apiQueryKeys.stockPools(key),
    queryFn: () => paged<StockPool>(`/api/v2/stock-pools?${new URLSearchParams(key)}`, limit, offset),
    refetchInterval: 30_000,
  });
}
export function useStockPool(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.stockPool(id),
    queryFn: () =>
      fetchJson<StockPool>(`/api/v2/stock-pools/${encodeURIComponent(id)}`),
    refetchInterval: 15_000,
  });
}
export function useStockPoolVersions(id: string, params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.stockPoolVersions(id, key),
    queryFn: () => paged<StockPoolVersion>(`/api/v2/stock-pools/${encodeURIComponent(id)}/versions?${new URLSearchParams(key)}`, limit, offset),
    refetchInterval: 15_000,
  });
}
export function useStockPoolRuns(id: string, params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    enabled: Boolean(id),
    queryKey: apiQueryKeys.stockPoolRuns(id, key),
    queryFn: () => paged<StockPoolRun>(`/api/v2/stock-pools/${encodeURIComponent(id)}/runs?${new URLSearchParams(key)}`, limit, offset),
    refetchInterval: 5_000,
  });
}
export function useStockPoolMembers(id: string, versionId: string, params: { selected?: boolean; status?: string; search?: string; limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 25;
  const offset = params.offset ?? 0;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (params.selected !== undefined) query.set("selected", String(params.selected));
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);
  const key = Object.fromEntries(query);
  return useQuery({
    enabled: Boolean(id && versionId),
    queryKey: apiQueryKeys.stockPoolMembers(id, versionId, key),
    queryFn: () => paged<StockPoolMember>(`/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/members?${query}`, limit, offset),
  });
}
export function useStockPoolDiff(id: string, versionId: string) {
  return useQuery({
    enabled: Boolean(id && versionId),
    queryKey: apiQueryKeys.stockPoolDiff(id, versionId),
    queryFn: () =>
      fetchJson<StockPoolDiff>(
        `/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/diff`,
      ),
  });
}
export function useStockPoolConsumptions(id: string, versionId: string, limit = 20, offset = 0) {
  const key = { limit: String(limit), offset: String(offset) };
  return useQuery({
    enabled: Boolean(id && versionId),
    queryKey: apiQueryKeys.stockPoolConsumptions(id, versionId, key),
    queryFn: () => paged<StockPoolConsumption>(`/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/consumptions?${new URLSearchParams(key)}`, limit, offset),
  });
}
export function useStockPoolPaperStrategies(id: string, versionId: string) {
  return useQuery({
    enabled: Boolean(id && versionId),
    queryKey: apiQueryKeys.stockPoolPaperStrategies(id, versionId),
    queryFn: () =>
      items<PaperExecutionStrategyVersion>(
        `/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/paper-execution-strategies`,
      ),
  });
}
export function createStockPool(input: CreateStockPoolInput) {
  return postTask<StockPool, CreateStockPoolInput>(
    "stock-pool-create",
    "/api/v2/stock-pools",
    input,
  );
}
export function archiveStockPool(id: string) {
  return putJson<StockPool, components["schemas"]["StockPoolUpdate"]>(
    `/api/v2/stock-pools/${encodeURIComponent(id)}`,
    { status: "archived" },
  );
}
export function runStockPool(id: string) {
  const body: components["schemas"]["RunCreate"] = {};
  return postTask<StockPoolRunResult, components["schemas"]["RunCreate"]>(
    `stock-pool-run:${id}`,
    `/api/v2/stock-pools/${encodeURIComponent(id)}/run`,
    body,
  );
}
export function updateStockPoolAutomation(id: string, enabled: boolean) {
  return postJson<StockPool, components["schemas"]["AutomationUpdate"]>(
    `/api/v2/stock-pools/${encodeURIComponent(id)}/automation`,
    { enabled },
  );
}
export type CreateStockPoolBacktestInput =
  components["schemas"]["PoolBacktestCreate"];
export type CreateStockPoolPaperRunInput =
  components["schemas"]["PoolPaperRunCreate"];
export function createStockPoolBacktest(
  id: string,
  versionId: string,
  input: CreateStockPoolBacktestInput,
) {
  return postTask<StockPoolBacktestResult, CreateStockPoolBacktestInput>(
    `stock-pool-backtest:${versionId}`,
    `/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/backtests`,
    input,
  );
}
export function createStockPoolPaperExecutionStrategy(id: string, versionId: string) {
  return postJson<PaperExecutionStrategyVersion, Record<string, never>>(
    `/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/paper-execution-strategies`,
    {},
  );
}
export function createStockPoolPaperRun(
  id: string,
  versionId: string,
  input: CreateStockPoolPaperRunInput,
) {
  return postTask<StockPoolPaperResult, CreateStockPoolPaperRunInput>(
    `stock-pool-paper:${versionId}`,
    `/api/v2/stock-pools/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/paper-runs`,
    input,
  );
}

